import { HugeiconsIcon } from '@hugeicons/react';
import {
  Mic01Icon,
  StopIcon,
  SentIcon,
  Loading02Icon,
  CheckmarkCircle02Icon,
  SparklesIcon,
  FileAttachmentIcon,
  RefreshIcon,
  PencilEdit02Icon,
} from '@hugeicons/core-free-icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { PageHeader } from '@/components/common/Bits';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { MotionSection } from '@/components/shared/MotionSection';
import { cn } from '@/lib/utils';

const STORAGE_KEY = 'via_draft_conversacion';

// Longitud mínima (en caracteres alfanuméricos) para considerar que un mensaje
// es "rico" y vale la pena enviarlo a la IA para repartirlo entre varios campos.
// Los audios y respuestas largas superan esto con holgura; un "sí" o "listo" no.
const AI_EXTRACTION_MIN_CHARS = 60;

// Mapa de los campos que devuelve la IA (backend) a las claves de `answers`
// que usa la conversación. Reutilizado por la extracción de audio/texto y de
// archivos para mantener un único punto de verdad.
const AI_FIELD_TO_ANSWER = {
  description: 'problema',
  use_case: 'que_haria_ia',
  beneficiaries: 'beneficiarios',
  data_types: 'datos',
  impact: 'impacto',
  requester: 'nombre_area',
  sponsor: 'sponsor',
  concerns: 'preocupaciones',
  additional: 'adicional',
};

// Conversation steps — VIA asks these questions one by one
const CONVERSATION_STEPS = [
  {
    id: 'bienvenida',
    via: '¡Hola! Soy VIA. Cuéntame tu idea de IA — escríbeme, háblame con el micrófono, o si tienes un documento con la información (un acta, una foto, un PDF), adjúntalo y yo me encargo de leerlo.',
    expectsInput: false,
    autoAdvance: true,
  },
  {
    id: 'nombre_area',
    via: '¿Cómo te llamas y de qué área o gerencia eres?',
    expectsInput: true,
    field: 'nombre_area',
    placeholder: 'Ej: Soy María García, de la Dirección Médica',
  },
  {
    id: 'sponsor',
    via: '¿Hay algún gerente o subgerente que respalda esta idea?',
    expectsInput: true,
    field: 'sponsor',
    placeholder: 'Ej: Mi jefe directo, Dr. Pérez, está de acuerdo',
  },
  {
    id: 'problema',
    via: 'Cuéntame: ¿qué problema o necesidad quieres resolver con IA?',
    expectsInput: true,
    field: 'problema',
    placeholder: 'Describe el problema con tus palabras...',
    required: true,
  },
  {
    id: 'que_haria_ia',
    via: '¿Qué imaginas que haría la IA? No necesitas ser técnico/a, solo cuéntame la idea general.',
    expectsInput: true,
    field: 'que_haria_ia',
    placeholder: 'Ej: Clasificar automáticamente los correos de pacientes...',
  },
  {
    id: 'beneficiarios',
    via: '¿A quién beneficiaría esto? ¿Pacientes, equipo interno, algún otro grupo?',
    expectsInput: true,
    field: 'beneficiarios',
    placeholder: 'Ej: Principalmente a los pacientes y al equipo de atención...',
  },
  {
    id: 'datos',
    via: '¿Qué tipo de información usaría? Por ejemplo: datos de pacientes, información financiera, datos públicos, o no estás seguro/a.',
    expectsInput: true,
    field: 'datos',
    placeholder: 'Ej: Usaría datos de la ficha clínica de pacientes...',
  },
  {
    id: 'impacto',
    via: '¿Qué impacto esperas si funciona bien? Puede ser ahorro de tiempo, mejor calidad, menos errores, o lo que tú veas.',
    expectsInput: true,
    field: 'impacto',
    placeholder: 'Ej: Reduciría el tiempo de respuesta de 48h a 4h...',
  },
  {
    id: 'preocupaciones',
    via: '¿Hay algo que te preocupe? Puede ser un riesgo, resistencia del equipo, o complejidad técnica. También está bien si no hay nada.',
    expectsInput: true,
    field: 'preocupaciones',
    placeholder: 'Ej: Me preocupa la privacidad de los datos...',
  },
  {
    id: 'adicional',
    via: '¿Algo más que quieras agregar? Contexto, urgencia, dependencias... o si no hay nada más, dime "listo" y cerramos.',
    expectsInput: true,
    field: 'adicional',
    placeholder: 'Cualquier detalle adicional, o escribe "listo"',
  },
  {
    id: 'cierre',
    via: '¡Perfecto! Ya tengo toda la información que necesito. Voy a registrar tu iniciativa. El equipo de IA te contactará en máximo 5 días hábiles. ¡Gracias por innovar!',
    expectsInput: false,
    isFinal: true,
  },
];

export default function ConversationIntake() {
  const navigate = useNavigate();
  const { user, hasAccess } = useAuth();
  const [messages, setMessages] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [inputText, setInputText] = useState('');
  const [answers, setAnswers] = useState({});
  const [draftId, setDraftId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [recording, setRecording] = useState(false);
  const [processingFile, setProcessingFile] = useState(false);
  // La IA está repartiendo un mensaje largo / audio entre los campos.
  const [aiProcessing, setAiProcessing] = useState(false);
  // Paso que el usuario está re-respondiendo (edición de una respuesta previa)
  const [editingStepId, setEditingStepId] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const initRef = useRef(false);
  // Evita el autoscroll en el primer render (que arrastraba la página y ocultaba
  // el título). Solo hacemos scroll tras la primera interacción del usuario.
  const didMountRef = useRef(false);

  // --- Scroll al último mensaje SOLO dentro del contenedor del chat ---
  // No usamos scrollIntoView (mueve toda la página). En su lugar llevamos el
  // scrollTop del propio contenedor de mensajes a su fondo, sin tocar el viewport.
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return; // no autoscroll en el montaje inicial
    }
    const el = messagesContainerRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
  }, [messages]);

  // --- Load saved draft ---
  useEffect(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.messages?.length > 0) {
          setMessages(parsed.messages);
          setAnswers(parsed.answers || {});
          setCurrentStep(parsed.currentStep || 0);
          if (parsed.draftId) setDraftId(parsed.draftId);
          return; // Don't init if we have saved state
        }
      } catch {
        /* noop */
      }
    }

    // Also try server draft
    api
      .get('/initiative-drafts/active', { params: { mode: 'conversacion' } })
      .then(({ data }) => {
        if (data && data.id) {
          const serverData = typeof data.data === 'string' ? JSON.parse(data.data) : data.data;
          if (serverData.messages?.length > 0) {
            setMessages(serverData.messages);
            setAnswers(serverData.answers || {});
            setCurrentStep(serverData.currentStep || 0);
            setDraftId(data.id);
            return;
          }
        }
        // No saved state — start fresh
        initConversation();
      })
      .catch(() => {
        initConversation();
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const initConversation = () => {
    if (initRef.current) return;
    initRef.current = true;
    const firstStep = CONVERSATION_STEPS[0];
    const secondStep = CONVERSATION_STEPS[1];
    setMessages([
      { role: 'via', text: firstStep.via, stepId: firstStep.id },
      { role: 'via', text: secondStep.via, stepId: secondStep.id },
    ]);
    setCurrentStep(1);
  };

  // --- Persist state ---
  const persistState = useCallback(async (msgs, ans, step, id) => {
    const state = { messages: msgs, answers: ans, currentStep: step, draftId: id };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));

    try {
      if (id) {
        await api.put(`/initiative-drafts/${id}`, { data: state, step });
      } else {
        const { data } = await api.post('/initiative-drafts', { mode: 'conversacion', data: state, step });
        setDraftId(data.id);
      }
    } catch {
      /* silent */
    }
  }, []);

  // --- Send user message ---
  const handleSend = () => {
    if (!inputText.trim() || aiProcessing) return;
    sendUserMessage(inputText.trim());
    setInputText('');
  };

  // Validación ligera de coherencia para pasos obligatorios.
  // Devuelve un texto de ayuda si la respuesta es insuficiente, o null si es válida.
  const validateAnswer = (step, text) => {
    if (!step?.required) return null;

    const clean = text.trim();

    // Respuestas demasiado cortas para un problema real
    if (clean.replace(/[^a-zA-ZÁ-Úá-ú0-9]/g, '').length < 10) {
      return 'Necesito un poco más de detalle para entenderte bien. ¿Puedes contarme con tus palabras qué problema o necesidad quieres resolver con IA?';
    }

    // Respuestas evasivas / sin contenido útil
    const evasive = /^(no s[eé]|nada|ninguno|ninguna|no|si|s[ií]|ok|listo|tal vez|quiz[aá]s?|no aplica|n\/a)\.?$/i;
    if (evasive.test(clean)) {
      return 'Entiendo, pero necesito entender el problema para poder ayudarte. Cuéntame, aunque sea brevemente: ¿qué situación te gustaría mejorar con IA?';
    }

    return null;
  };

  const sendUserMessage = (text) => {
    // --- Modo edición: el usuario re-responde una pregunta anterior ---
    if (editingStepId) {
      const editStep = CONVERSATION_STEPS.find((s) => s.id === editingStepId);
      if (editStep) {
        const hint = validateAnswer(editStep, text);
        if (hint) {
          setMessages((prev) => [...prev, { role: 'via', text: hint, stepId: `${editStep.id}_retry` }]);
          return;
        }
        const editedMessages = [...messages, { role: 'user', text, stepId: editStep.id }];
        const editedAnswers = { ...answers, [editStep.field]: text };
        const ackMessages = [
          ...editedMessages,
          {
            role: 'via',
            text: 'Listo, actualicé esa respuesta. Puedes seguir editando otras o continuar donde estabas.',
            stepId: 'edit_ack',
          },
        ];
        setMessages(ackMessages);
        setAnswers(editedAnswers);
        setEditingStepId(null);
        persistState(ackMessages, editedAnswers, currentStep, draftId);
        setTimeout(() => inputRef.current?.focus(), 300);
      }
      return;
    }

    const step = CONVERSATION_STEPS[currentStep];
    if (!step) return;

    // Validación de coherencia: si es insuficiente, repreguntamos sin avanzar
    const hint = validateAnswer(step, text);
    if (hint) {
      const retryMessages = [
        ...messages,
        { role: 'user', text, stepId: step.id },
        { role: 'via', text: hint, stepId: `${step.id}_retry` },
      ];
      setMessages(retryMessages);
      persistState(retryMessages, answers, currentStep, draftId);
      return;
    }

    // Ruta inteligente: si el mensaje es lo bastante rico (típico de un audio o
    // una respuesta larga que cubre varios temas), pedimos a la IA que reparta
    // el contenido entre todos los campos y saltamos las preguntas ya cubiertas.
    if (shouldUseAiExtraction(step, text)) {
      handleRichMessage(text, step);
      return;
    }

    // --- Flujo secuencial clásico (respuestas cortas o fallback) ---
    advanceOneStep(text, step);
  };

  // ¿Vale la pena mandar este mensaje a la IA para repartirlo en varios campos?
  const shouldUseAiExtraction = (step, text) => {
    if (!step?.field) return false;
    const alnum = text.replace(/[^a-zA-ZÁ-Úá-ú0-9]/g, '').length;
    return alnum >= AI_EXTRACTION_MIN_CHARS;
  };

  // Devuelve el índice del primer paso con `field` cuya respuesta sigue vacía,
  // buscando SOLO desde `from` en adelante. Con esto saltamos las preguntas ya
  // cubiertas (por la IA o por respuestas previas) y nunca repreguntamos hacia
  // atrás algo que el usuario ya contestó.
  const firstUnansweredStep = (ans, from = 0) => {
    for (let i = Math.max(from, 0); i < CONVERSATION_STEPS.length; i++) {
      const s = CONVERSATION_STEPS[i];
      if (s.field && !String(ans[s.field] || '').trim()) return i;
    }
    // Nada pendiente hacia adelante → ir al cierre (último paso).
    return CONVERSATION_STEPS.length - 1;
  };

  // Guarda la respuesta literal en su campo y avanza al PRÓXIMO paso sin
  // responder (no simplemente currentStep+1). Así, aunque una extracción previa
  // por IA ya haya llenado campos más adelante, no se repregunta nada cubierto.
  const advanceOneStep = (text, step) => {
    const newMessages = [...messages, { role: 'user', text, stepId: step.id }];
    setMessages(newMessages);

    const newAnswers = { ...answers, [step.field]: text };
    setAnswers(newAnswers);

    // Saltar a la siguiente pregunta sin responder, buscando después del paso actual.
    const nextStepIdx = firstUnansweredStep(newAnswers, currentStep + 1);
    setCurrentStep(nextStepIdx);

    const nextStep = CONVERSATION_STEPS[nextStepIdx];
    setTimeout(() => {
      setMessages((prev) => [...prev, { role: 'via', text: nextStep.via, stepId: nextStep.id }]);
      persistState(
        [...newMessages, { role: 'via', text: nextStep.via, stepId: nextStep.id }],
        newAnswers,
        nextStepIdx,
        draftId,
      );
      setTimeout(() => inputRef.current?.focus(), 300);
    }, 800);
  };

  // Etiquetas legibles para confirmar al usuario lo que la IA entendió.
  const ANSWER_LABELS = {
    problema: 'el problema a resolver',
    que_haria_ia: 'qué haría la IA',
    beneficiarios: 'los beneficiarios',
    datos: 'el tipo de datos',
    impacto: 'el impacto esperado',
    nombre_area: 'quién eres y tu área',
    sponsor: 'el sponsor',
    preocupaciones: 'las preocupaciones',
    adicional: 'contexto adicional',
  };

  // Procesa un mensaje rico con la IA: reparte el texto en campos, salta las
  // preguntas cubiertas y solo pregunta lo que falta. Si la IA falla, cae al
  // flujo secuencial para no bloquear al usuario.
  const handleRichMessage = async (text, step) => {
    const baseMessages = [...messages, { role: 'user', text, stepId: step.id }];
    setMessages(baseMessages);
    setAiProcessing(true);

    try {
      const { data } = await api.post('/extract-conversation', { text, answers }, { timeout: 28000 });
      const fields = data?.fields || {};

      // Fusionar campos extraídos SIN pisar respuestas previas con vacío.
      const merged = { ...answers };
      let filledCount = 0;
      const newlyFilled = [];
      for (const [aiKey, answerKey] of Object.entries(AI_FIELD_TO_ANSWER)) {
        const val = String(fields[aiKey] || '').trim();
        if (!val) continue;
        const wasEmpty = !String(merged[answerKey] || '').trim();
        merged[answerKey] = val;
        filledCount++;
        if (wasEmpty && ANSWER_LABELS[answerKey]) newlyFilled.push(ANSWER_LABELS[answerKey]);
      }

      // Si la IA no logró extraer nada útil, guardamos el texto en el paso actual
      // y seguimos el flujo secuencial (fallback suave).
      if (filledCount === 0) {
        setAiProcessing(false);
        advanceOneStep(text, step);
        return;
      }

      const nextIdx = firstUnansweredStep(merged);
      const nextStep = CONVERSATION_STEPS[nextIdx];

      // Mensaje de confirmación de lo que entendió (transparencia, opción a).
      const captured = newlyFilled.length > 0 ? `Anoté ${listToText(newlyFilled)}.` : 'Actualicé tu información.';
      const followUp = nextStep && nextStep.field ? ` Ahora cuéntame: ${nextStep.via}` : ` ${nextStep?.via || ''}`;

      const aiMessages = [
        ...baseMessages,
        { role: 'via', text: `${captured}${followUp}`.trim(), stepId: nextStep?.id || 'cierre' },
      ];
      setMessages(aiMessages);
      setAnswers(merged);
      setCurrentStep(nextIdx);
      setAiProcessing(false);
      persistState(aiMessages, merged, nextIdx, draftId);
      setTimeout(() => inputRef.current?.focus(), 300);
    } catch {
      // La IA no está disponible: no perdemos el mensaje, seguimos secuencial.
      setAiProcessing(false);
      advanceOneStep(text, step);
    }
  };

  // --- Reiniciar conversación desde cero (confirmación vía AlertDialog) ---
  const handleReset = async () => {
    if (submitting || resetting) return;
    setResetting(true);
    try {
      // Descartar el borrador conocido por el front.
      if (draftId) {
        await api.delete(`/initiative-drafts/${draftId}`).catch(() => {});
      }
      // Y, por seguridad, descartar CUALQUIER borrador activo que el servidor
      // tenga para este modo aunque su id difiera del que conoce el front
      // (si no, al recargar el useEffect volvería a cargar ese borrador y la
      // conversación "reaparecería" pese al reinicio).
      try {
        const { data } = await api.get('/initiative-drafts/active', { params: { mode: 'conversacion' } });
        if (data && data.id && data.id !== draftId) {
          await api.delete(`/initiative-drafts/${data.id}`).catch(() => {});
        }
      } catch {
        /* sin borrador activo o endpoint no disponible: nada que borrar */
      }
    } finally {
      sessionStorage.removeItem(STORAGE_KEY);
      setDraftId(null);
      setAnswers({});
      setInputText('');
      setEditingStepId(null);
      setMessages([]);
      setCurrentStep(0);
      initRef.current = false;
      initConversation();
      setResetting(false);
      setResetConfirmOpen(false);
      toast.success('Conversación reiniciada. Empecemos de nuevo.');
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  };

  // --- Editar (re-responder) una pregunta ya contestada ---
  const handleEditAnswer = (stepId) => {
    const step = CONVERSATION_STEPS.find((s) => s.id === stepId);
    if (!step?.field) return;
    setEditingStepId(stepId);
    setInputText(answers[step.field] || '');
    setMessages((prev) => [
      ...prev,
      {
        role: 'via',
        text: `Vamos a mejorar tu respuesta a: "${step.via}" Escribe la nueva versión con todo el contexto que quieras agregar.`,
        stepId: `${stepId}_edit_prompt`,
      },
    ]);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const cancelEdit = () => {
    setEditingStepId(null);
    setInputText('');
  };

  // --- Audio: Web Speech API (transcripción en el navegador) ---
  const recognitionRef = useRef(null);
  const [interimText, setInterimText] = useState('');

  const startRecording = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error('Tu navegador no soporta reconocimiento de voz. Usa Chrome, Edge o Safari.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'es-CL';
    recognition.continuous = true;
    recognition.interimResults = true;

    let finalTranscript = '';

    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interim += transcript;
        }
      }
      setInterimText(finalTranscript + interim);
    };

    recognition.onerror = (event) => {
      if (event.error === 'not-allowed') {
        toast.error('Permiso de micrófono denegado. Habilítalo en la configuración del navegador.');
      } else if (event.error !== 'aborted') {
        toast.error('Error en reconocimiento de voz. Intenta de nuevo.');
      }
      setRecording(false);
    };

    recognition.onend = () => {
      // Send the transcribed text as a message if we got anything
      if (finalTranscript.trim()) {
        sendUserMessage(finalTranscript.trim());
      }
      setRecording(false);
      setInterimText('');
    };

    recognitionRef.current = recognition;
    recognition.start();
    setRecording(true);
    setInterimText('');
  };

  const stopRecording = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
  };

  // Abre el selector de archivos. Lo encapsulamos para asegurar que el .click()
  // ocurra sincrónicamente dentro del gesto del usuario (requisito de Safari).
  const handleAttachClick = () => {
    if (processingFile || editingStepId) return;
    fileInputRef.current?.click();
  };

  // --- File upload: todos los archivos (texto, PDF, imagen, Word) van por el
  // flujo de documento /extract-from-file, que usa el prompt de extracción
  // documental (mejor para documentos largos y estructurados como un BRD) y
  // hace polling del job async. El backend reconoce .md/.txt/.csv como texto. ---
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('El archivo es demasiado grande (máximo 10MB)');
      return;
    }

    // Show file message in chat
    const fileMsg = `📎 ${file.name}`;
    setMessages((prev) => [...prev, { role: 'user', text: fileMsg, isFile: true }]);
    setProcessingFile(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await api.post('/extract-from-file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // El backend corre síncrono detrás de API Gateway (techo ~29s). Damos un
        // pelín más para no cortar nosotros antes de que llegue la respuesta.
        timeout: 32000,
      });

      if (data?.status === 'completed' && (data.summary || data.fields)) {
        // Respuesta síncrona (caso futuro): usar directamente.
        handleExtractionSuccess(data, file.name);
      } else if (data?.job_id && (data?.status === 'processing' || data?.status === 'accepted')) {
        // El backend crea un job async; hacemos polling hasta tener resultado.
        const result = await pollExtractionJob(data.job_id);
        if (result) {
          handleExtractionSuccess(result, file.name);
        } else {
          throw new Error('processing_failed');
        }
      } else {
        throw new Error('unexpected_response');
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: 'via',
          text: 'No pude procesar el archivo en este momento. Puedes contarme tu idea por texto o intentar de nuevo.',
          stepId: 'file_error',
        },
      ]);
    } finally {
      setProcessingFile(false);
    }
  };

  // Poll extraction job until completed or failed (max 90 seconds)
  const pollExtractionJob = async (jobId) => {
    const maxAttempts = 30; // 30 * 3s = 90 seconds max
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const { data } = await api.get(`/extract-job/${jobId}`);
        if (data.status === 'completed' && data.result) {
          return data.result;
        }
        if (data.status === 'failed') {
          return null;
        }
        // Still processing, continue polling
      } catch {
        // Network error, retry
      }
    }
    return null; // Timeout
  };

  // Handle successful extraction (ruta binaria: PDF, imagen, docx)
  const handleExtractionSuccess = (data, fileName) => {
    // For /extract-fields response (flat fields)
    const summary = data.summary || data.roi_estimate || data.description || 'Información extraída del documento';
    const fields = data.fields || data;

    const extractedMsg = `Revisé "${fileName}". Esto es lo que entendí:\n\n${summary}\n\nRepasa el detalle y, si todo está bien, registra la solicitud. También puedes editar cualquier respuesta antes de enviar.`;

    // Map fields to answers usando el mapeo compartido, con algunos alias
    // extra propios de la extracción de archivos (name, roi_estimate, business_owner).
    const extractedAnswers = { _from_file: fileName };
    for (const [aiKey, answerKey] of Object.entries(AI_FIELD_TO_ANSWER)) {
      const val = String(fields[aiKey] || '').trim();
      if (val) extractedAnswers[answerKey] = val;
    }
    // Alias/fallbacks para no perder datos cuando el modelo usa otras claves.
    if (!extractedAnswers.problema && fields.name) extractedAnswers.problema = fields.name;
    if (!extractedAnswers.problema && fields.description) extractedAnswers.problema = fields.description;
    if (!extractedAnswers.impacto && fields.roi_estimate) extractedAnswers.impacto = fields.roi_estimate;
    if (!extractedAnswers.nombre_area && fields.business_owner) extractedAnswers.nombre_area = fields.business_owner;
    if (!extractedAnswers.nombre_area && fields.department) extractedAnswers.nombre_area = fields.department;

    const merged = { ...answers, ...extractedAnswers };
    setAnswers(merged);
    setCurrentStep(CONVERSATION_STEPS.length - 1);
    setMessages((prev) => {
      const next = [...prev, { role: 'via', text: extractedMsg, stepId: 'file_extracted' }];
      persistState(next, merged, CONVERSATION_STEPS.length - 1, draftId);
      return next;
    });
  };

  // --- Submit: create demand intake ---
  const handleSubmit = async () => {
    if (!answers.problema?.trim()) {
      toast.error('Necesitamos al menos saber qué problema quieres resolver');
      return;
    }

    setSubmitting(true);
    try {
      // Build demand intake from conversation answers
      const demandIntake = {
        title: (answers.problema || '').slice(0, 120),
        description: [
          answers.problema,
          answers.que_haria_ia && `Lo que haría la IA: ${answers.que_haria_ia}`,
          answers.beneficiarios && `Beneficiarios: ${answers.beneficiarios}`,
        ]
          .filter(Boolean)
          .join('\n\n'),
        origin: 'plataforma',
        source_contact: extractName(answers.nombre_area) || user?.name || '',
        department: extractArea(answers.nombre_area) || 'Por definir',
        urgency: 'Normal',
        notes: [
          answers.impacto && `Impacto esperado: ${answers.impacto}`,
          answers.preocupaciones && `Preocupaciones: ${answers.preocupaciones}`,
          answers.adicional && `Contexto adicional: ${answers.adicional}`,
          answers.sponsor && `Patrocinador: ${answers.sponsor}`,
          answers.datos && `Tipo de datos: ${answers.datos}`,
        ]
          .filter(Boolean)
          .join('\n'),
        provider_detected: false,
        intake_mode: 'conversacion',
        extended_data: answers,
      };

      const { data } = await api.post('/demanda', demandIntake);

      // Mark draft as converted
      if (draftId) {
        await api
          .put(`/initiative-drafts/${draftId}`, {
            data: { messages, answers, currentStep },
            step: currentStep,
            status: 'completado',
          })
          .catch(() => {});
      }

      sessionStorage.removeItem(STORAGE_KEY);
      toast.success('¡Solicitud registrada! El equipo de IA te contactará pronto.');
      // La bandeja de demanda no es accesible para todos los roles (p. ej. evaluador).
      // Redirigimos a la bandeja solo si el usuario tiene acceso; si no, al inicio.
      navigate(hasAccess('/demanda') ? '/demanda' : '/');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Error al registrar la solicitud');
    } finally {
      setSubmitting(false);
    }
  };

  // --- Handle key press ---
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // --- Check if conversation is complete ---
  const isFinalStep =
    currentStep >= CONVERSATION_STEPS.length - 1 && CONVERSATION_STEPS[CONVERSATION_STEPS.length - 1].isFinal;
  const showFinalMessage =
    messages.some((m) => m.stepId === 'cierre') || messages.some((m) => m.stepId === 'file_extracted');
  const currentStepData = CONVERSATION_STEPS[currentStep] || null;
  const expectsInput = currentStepData?.expectsInput ?? false;

  return (
    <div className="max-w-2xl mx-auto px-6 py-6 flex flex-col">
      {/* Header */}
      <MotionSection className="flex-shrink-0">
        <PageHeader
          title="Conversación con VIA"
          subtitle="Cuéntame tu idea como si estuviéramos en una reunión rápida."
          testId="conversation-header"
          backTo="/demanda/nueva"
          backLabel="Volver a opciones"
        />
      </MotionSection>

      {/* Chat messages — altura automática con un tope: con poca conversación
          ocupa solo lo necesario (sin vacío gigante); cuando crece, scrollea
          internamente sin generar scroll de la página ni un segundo scrollbar. */}
      <div ref={messagesContainerRef} className="space-y-3 py-4 pr-1 overflow-y-auto max-h-[calc(100vh-20rem)]">
        {messages.map((msg, i) => {
          // ¿Esta respuesta del usuario corresponde a un paso editable con campo?
          const stepForMsg =
            msg.role === 'user' && msg.stepId ? CONVERSATION_STEPS.find((s) => s.id === msg.stepId) : null;
          const canEdit = Boolean(stepForMsg?.field) && !msg.isFile && !editingStepId && !submitting;
          return (
            <div key={i} className={cn('flex gap-2.5 group', msg.role === 'user' ? 'justify-end' : 'justify-start')}>
              {msg.role === 'via' && (
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                  <HugeiconsIcon icon={SparklesIcon} size={14} strokeWidth={1.5} className="text-primary" />
                </div>
              )}
              <div className="flex items-center gap-1.5 max-w-[80%] min-w-0">
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => handleEditAnswer(msg.stepId)}
                    title="Editar esta respuesta"
                    aria-label="Editar esta respuesta"
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10">
                    <HugeiconsIcon icon={PencilEdit02Icon} size={13} strokeWidth={1.5} />
                  </button>
                )}
                <div
                  className={cn(
                    'rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap min-w-0 break-words [overflow-wrap:anywhere]',
                    msg.role === 'via'
                      ? 'bg-muted/50 text-foreground rounded-tl-sm'
                      : 'bg-primary text-primary-foreground rounded-tr-sm',
                  )}>
                  {msg.text}
                </div>
              </div>
            </div>
          );
        })}

        {recording && interimText && (
          <div className="flex gap-2.5 justify-end">
            <div className="max-w-[80%] rounded-2xl px-4 py-2.5 text-sm bg-primary/10 text-foreground rounded-tr-sm italic">
              {interimText}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="flex-shrink-0 border-t border-border pt-3">
        {editingStepId && (
          <div className="mb-2 flex items-center justify-between gap-2 rounded-lg bg-secondary/10 px-3 py-1.5 text-xs text-secondary">
            <span className="inline-flex items-center gap-1.5">
              <HugeiconsIcon icon={PencilEdit02Icon} size={13} strokeWidth={1.5} />
              Editando una respuesta anterior
            </span>
            <button
              type="button"
              onClick={cancelEdit}
              className="font-medium text-muted-foreground hover:text-foreground transition-colors">
              Cancelar
            </button>
          </div>
        )}
        {showFinalMessage && !editingStepId ? (
          <div className="flex items-center gap-2">
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground">
              {submitting ? (
                <>
                  <HugeiconsIcon icon={Loading02Icon} size={16} strokeWidth={1.5} className="animate-spin mr-1.5" />
                  Enviando...
                </>
              ) : (
                <>
                  <HugeiconsIcon icon={CheckmarkCircle02Icon} size={16} strokeWidth={1.5} className="mr-1.5" />
                  Enviar solicitud
                </>
              )}
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-[44px] w-[44px] rounded-xl flex-shrink-0"
              onClick={() => setResetConfirmOpen(true)}
              disabled={resetting || submitting}
              title="Reiniciar la conversación desde cero"
              data-testid="conversation-reset-final">
              <HugeiconsIcon
                icon={resetting ? Loading02Icon : RefreshIcon}
                size={18}
                strokeWidth={1.5}
                className={cn(resetting && 'animate-spin')}
              />
            </Button>
          </div>
        ) : expectsInput || editingStepId ? (
          <div className="flex items-start gap-2">
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  editingStepId
                    ? 'Reescribe tu respuesta con más contexto...'
                    : currentStepData?.placeholder || 'Escribe tu respuesta...'
                }
                rows={1}
                className={cn(
                  'w-full resize-none rounded-xl border border-border bg-background px-4 py-2.5 pr-10 text-sm leading-6',
                  'placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40',
                  'h-[44px] max-h-[120px]',
                )}
                style={{ overflow: 'hidden' }}
                onInput={(e) => {
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(Math.max(e.target.scrollHeight, 44), 120) + 'px';
                }}
              />
            </div>

            {/* Reset button */}
            {messages.length > 0 && !editingStepId && (
              <Button
                variant="outline"
                size="icon"
                className="h-[44px] w-[44px] rounded-xl flex-shrink-0"
                onClick={() => setResetConfirmOpen(true)}
                disabled={resetting || submitting}
                title="Reiniciar la conversación desde cero"
                data-testid="conversation-reset">
                <HugeiconsIcon
                  icon={resetting ? Loading02Icon : RefreshIcon}
                  size={18}
                  strokeWidth={1.5}
                  className={cn(resetting && 'animate-spin')}
                />
              </Button>
            )}

            {/* File attach button.
                El input vive fuera del flujo visible pero NO con `hidden`
                (display:none): en algunos navegadores un input display:none no
                abre el selector al invocar .click(). Lo posicionamos fuera de
                pantalla pero "presente". El accept usa solo extensiones (sin
                comodín text/*) por máxima compatibilidad en Safari/macOS. */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp,.txt,.md,.markdown,.csv,.rtf"
              className="sr-only"
              tabIndex={-1}
              aria-hidden="true"
              onChange={handleFileSelect}
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-[44px] w-[44px] rounded-xl flex-shrink-0"
              onClick={handleAttachClick}
              disabled={processingFile || Boolean(editingStepId)}
              title="Adjuntar archivo (PDF, Word, imagen, texto o Markdown)">
              <HugeiconsIcon icon={FileAttachmentIcon} size={18} strokeWidth={1.5} />
            </Button>

            {/* Mic button */}
            <Button
              variant={recording ? 'destructive' : 'outline'}
              size="icon"
              className="h-[44px] w-[44px] rounded-xl flex-shrink-0"
              onClick={recording ? stopRecording : startRecording}
              title={recording ? 'Detener grabación' : 'Grabar audio'}>
              <HugeiconsIcon
                icon={recording ? StopIcon : Mic01Icon}
                size={18}
                strokeWidth={1.5}
                className={recording ? 'animate-pulse' : ''}
              />
            </Button>

            {/* Send button */}
            <Button
              size="icon"
              className="h-[44px] w-[44px] rounded-xl flex-shrink-0 bg-primary hover:bg-primary/90"
              onClick={handleSend}
              disabled={!inputText.trim() || aiProcessing}>
              <HugeiconsIcon
                icon={aiProcessing ? Loading02Icon : SentIcon}
                size={18}
                strokeWidth={1.5}
                className={aiProcessing ? 'animate-spin' : ''}
              />
            </Button>
          </div>
        ) : (
          <div className="text-center py-2">
            <span className="text-xs text-muted-foreground">VIA está pensando...</span>
          </div>
        )}

        {/* Recording indicator */}
        {recording && (
          <div className="mt-2 flex items-center gap-2 text-xs text-primary">
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            Escuchando... Habla y presiona el botón para enviar
          </div>
        )}
        {processingFile && (
          <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
            <HugeiconsIcon icon={Loading02Icon} size={12} strokeWidth={1.5} className="animate-spin" />
            Analizando documento con IA... Esto puede tomar unos segundos.
          </div>
        )}
        {aiProcessing && (
          <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
            <HugeiconsIcon icon={Loading02Icon} size={12} strokeWidth={1.5} className="animate-spin" />
            Procesando lo que me contaste y ordenando la información...
          </div>
        )}
      </div>

      {/* Confirmación de reinicio */}
      <AlertDialog open={resetConfirmOpen} onOpenChange={(open) => !resetting && setResetConfirmOpen(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reiniciar conversación</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Seguro que quieres reiniciar la conversación? Se borrará todo lo que has escrito y empezaremos de nuevo.
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resetting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleReset();
              }}
              disabled={resetting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {resetting ? 'Reiniciando...' : 'Reiniciar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// --- Helpers: extract name/area from free text ---
function extractName(text) {
  if (!text) return '';
  // Try to get name before common separators
  const match = text.match(/(?:soy\s+)?([A-ZÁ-Ú][a-zá-ú]+ [A-ZÁ-Ú][a-zá-ú]+)/i);
  return match
    ? match[1]
    : text
        .split(/[,.]|de la|del/i)[0]
        .replace(/^soy\s+/i, '')
        .trim();
}

function extractArea(text) {
  if (!text) return '';
  const match = text.match(/(?:de la|del|de)\s+(.+)/i);
  return match ? match[1].trim() : '';
}

// Une una lista de frases en un texto natural en español: "a, b y c".
function listToText(items) {
  const list = items.filter(Boolean);
  if (list.length === 0) return '';
  if (list.length === 1) return list[0];
  return `${list.slice(0, -1).join(', ')} y ${list[list.length - 1]}`;
}
